/**
 * Resume Scorecard — deterministic, client-side resume quality analysis.
 *
 * Runs entirely in the browser on the in-memory ResumeJSON (no API call, no
 * cost, works on unsaved edits), except the "Accuracy" dimension, which is fed
 * by the server's deterministic lint pass (spelling/grammar/duplication — still
 * no LLM).
 *
 * Scoring model (owner decision 2026-07-26):
 *   • Completeness 15% · Accuracy 45% · Authenticity 20% · Quantification 20%
 *   • A suspected TYPO or GRAMMAR mistake is always severity 'high'.
 *   • Any UNRESOLVED 'high' finding ZEROES its dimension. Marking a finding
 *     resolved removes it from every calculation, so the score recovers.
 *
 * Dimension routing (owner decision 2026-07-31): the zero-out rule is GLOBAL —
 * a 'high' zeroes WHATEVER dimension it belongs to — so a finding must land in
 * exactly ONE dimension, and the right one. The server lint finding's `category`
 * is the routing key (`LINT_CATEGORY_DIMENSION`):
 *
 *   correctness, consistency  → Accuracy       (spelling, tense, duplicates…)
 *   completeness              → Completeness   (too few skills…)
 *   style, credibility        → Authenticity   (etc-lists, hyperbole, repetition…)
 *
 * Before this, EVERY lint finding was ingested by Accuracy regardless of
 * category, so a stylistic 'high' zeroed the accuracy score. Severity promotion
 * (`effectiveSeverity`) is deliberately independent of routing: promoting a
 * finding to 'high' never moves it to another dimension.
 *
 * This is a guide, not a gate. It detects patterns; it does not rewrite.
 */
import type { ExperienceBullet, ResumeJSON, Skill } from '../types';

export type Severity = 'high' | 'med' | 'low';

/**
 * Pointer from a finding back to the editable resume item it came from, so the
 * UI can show that item in an editable state and write changes back. Findings
 * with no single source item (completeness checks, cross-bullet density) carry
 * no path and are read-only.
 */
export type FindingPath =
  | { kind: 'summary' }
  | { kind: 'highlight'; index: number }
  | { kind: 'summary_phrase'; index: number }
  | { kind: 'skill'; index: number }
  /**
   * The PROSE blurb under a company heading (`experience[i].description`) —
   * NOT the company name. The linter lints it as prose (hyperbole, spelling,
   * tense…), so it needs a real editor; the company NAME on the same anchor is
   * a proper noun and stays read-only. See `lintFindingPath`.
   */
  | { kind: 'companyDescription'; companyIndex: number }
  /**
   * The TITLE of one item in a titled list section (an award, a degree, a talk…).
   * Owner request 2026-07-27: a multi-item finding spanning two award titles
   * rendered them as read-only quotes ("I'd like to update one of the
   * inconsistent Awards records, but this control is non-editable?").
   *
   * TITLE ONLY. These sections carry SEVERAL linted fields under the SAME path
   * (`awards[0]` is title + issuer + reward), so the field is pinned in the path
   * grammar — otherwise an `issuer` finding would open an editor loaded with the
   * TITLE and its Save would overwrite the wrong field. See `lintFindingPath`.
   */
  | { kind: 'itemTitle'; section: ItemTitleSection; index: number }
  | { kind: 'bullet'; companyIndex: number; roleIndex: number; bulletIndex: number };

/** Titled list sections whose item TITLE the scorecard can edit in place. */
export const ITEM_TITLE_SECTIONS = [
  'education', 'awards', 'presentations', 'publications', 'portfolios',
] as const;

export type ItemTitleSection = (typeof ITEM_TITLE_SECTIONS)[number];

export interface Finding {
  id?: string;        // stable identity for resolved-tracking (always assigned in buildScorecard)
  severity: Severity;
  /**
   * Server lint rule id ('spelling', 'hyperbole', …) when the finding came from
   * the linter; absent on the browser-computed dimensions. Carried so the row
   * can offer rule-specific actions (Add to My Dictionary). NEVER used to pick
   * the render tier — that stays derived from what the finding CARRIES.
   */
  rule?: string;
  /** The exact token a word-level rule flagged, when the server supplies one. */
  word?: string;
  label: string;      // what was found, e.g. '"results-driven"'
  section: string;    // where, e.g. 'Summary' / 'Experience — Acme Inc.'
  detail?: string;    // optional snippet for context
  path?: FindingPath; // present when the finding maps to one editable item
  /**
   * Raw server lint path grammar (`experience[0].roles[1].bullets[2]`), kept so
   * the UI can humanize it and expand multi-entry findings. Deliberately NOT
   * part of the finding id — ids must stay byte-identical across releases so
   * users' persisted "resolved" dismissals keep matching.
   */
  rawPath?: string;
  /**
   * Every item a MULTI-item finding spans (see `LintFinding.related`). Carried
   * through from the server finding so the modal can render one editable,
   * deletable block per item instead of only the one `path` points at.
   */
  related?: RelatedItem[];
  /** Score cost while this finding is UNRESOLVED (internal to the scoring pass). */
  penalty?: number;
  /** Scoring group for rules that aren't a plain penalty sum. */
  group?: 'rule_of_three' | 'unquantified' | 'missing_section';
}

/**
 * A finding from the server-side deterministic linter
 * (`infrastructure/lambdas/shared/resume_lint.py`, returned by
 * `POST /resumes/{name}/review` with `mode: 'lint'`). Same shape as the Python
 * LintFinding dict — spelling, doubled words, tense drift, duplicate skills…
 */
export interface LintFinding {
  rule: string;
  severity: Severity;
  category: string;
  /** Recommendation path grammar, e.g. `experience[0].roles[1].bullets[2]`; '' = resume-wide. */
  path: string;
  field: string;
  message: string;
  snippet?: string | null;
  suggestion?: string | null;
  can_fix?: boolean;
  /**
   * OPTIONAL structured token for word-level rules (spelling). Preferred over
   * parsing the message when the server supplies it; the client falls back to
   * the quoted token in `message` so this works before/after that server field
   * exists.
   */
  word?: string | null;
  /**
   * OPTIONAL, present ONLY on findings that span MULTIPLE resume items (absent —
   * not empty — on single-item ones). Lists EVERY involved item in resume order,
   * INCLUDING the one `path` already points at, so the UI can show and act on
   * the whole set (the "merge these two skills" case). Populated server-side for
   * `duplicate_skill`, `repeated_ngram` and `multiplier_notation`.
   */
  related?: RelatedItem[];
}

/** One item of a multi-item finding (`LintFinding.related`). */
export interface RelatedItem {
  /** Lint path grammar for this item, e.g. `skills[3]`. */
  path: string;
  /** Field within the item ('title' for a skill, 'text' for a bullet). */
  field: string;
  /** That item's current display text, whitespace-collapsed + truncated server-side. */
  label: string;
}

export interface DimensionScore {
  key: 'authenticity' | 'correctness' | 'quantification' | 'completeness';
  label: string;
  score: number | null;   // 0-100, or null when not applicable
  blurb: string;
  findings: Finding[];
  /** >0 when the score was forced to 0 by that many unresolved 'high' findings. */
  zeroedBy?: number;
}

export interface Scorecard {
  overall: number;
  dimensions: DimensionScore[];
}

/**
 * Internal shape: findings are produced FIRST (so ids can be assigned), then
 * each dimension is scored from the findings that are still unresolved.
 */
interface DimensionBuilder {
  key: DimensionScore['key'];
  label: string;
  findings: Finding[];
  finalize: (kept: Finding[]) => { score: number | null; blurb: string };
}

// ── Prose collection ───────────────────────────────────────────────────────
interface Snippet { text: string; section: string; isBullet: boolean; path: FindingPath; }

const included = (v?: string) => v !== '0';

function collectProse(resume: ResumeJSON): Snippet[] {
  const out: Snippet[] = [];
  if (resume.summary?.trim()) out.push({ text: resume.summary, section: 'Summary', isBullet: false, path: { kind: 'summary' } });
  (resume.highlights || []).forEach((h, hi) => {
    if (h?.trim()) out.push({ text: h, section: 'Highlights', isBullet: true, path: { kind: 'highlight', index: hi } });
  });
  (resume.summary_phrases || []).forEach((p, pi) => {
    if (included(p.include) && p.text?.trim()) out.push({ text: p.text, section: 'Summary phrase', isBullet: false, path: { kind: 'summary_phrase', index: pi } });
  });
  (resume.experience || []).forEach((co, ci) => {
    if (!included(co.include)) return;
    const cname = co.company || 'Experience';
    (co.roles || []).forEach((role, ri) => {
      if (!included(role.include)) return;
      (role.bullets || []).forEach((b, bi) => {
        if (included(b.include) && b.text?.trim()) {
          out.push({ text: b.text, section: `Experience — ${cname}`, isBullet: true, path: { kind: 'bullet', companyIndex: ci, roleIndex: ri, bulletIndex: bi } });
        }
      });
    });
  });
  return out;
}

/**
 * The items of a titled list section, as a minimally-typed read view.
 *
 * The five sections have different item shapes (an Award has issuer/reward, a
 * Publication doesn't) but all carry `title`, which is the only field addressed
 * here. Writes go through `setTextAtPath`, which spreads the ORIGINAL item, so
 * nothing outside `title` is ever narrowed or dropped.
 */
type TitledItem = { title?: string };
function itemTitleList(resume: ResumeJSON, section: ItemTitleSection): TitledItem[] {
  return (resume[section] as TitledItem[] | undefined) || [];
}

/** Read the current text behind a finding path from the (live) resume. */
export function getTextAtPath(resume: ResumeJSON, path: FindingPath): string {
  switch (path.kind) {
    case 'summary': return resume.summary || '';
    case 'highlight': return (resume.highlights || [])[path.index] ?? '';
    case 'summary_phrase': return (resume.summary_phrases || [])[path.index]?.text ?? '';
    case 'skill': return (resume.skills || [])[path.index]?.title ?? '';
    case 'companyDescription': return (resume.experience || [])[path.companyIndex]?.description ?? '';
    case 'itemTitle': return itemTitleList(resume, path.section)[path.index]?.title ?? '';
    case 'bullet': return (resume.experience || [])[path.companyIndex]?.roles?.[path.roleIndex]?.bullets?.[path.bulletIndex]?.text ?? '';
  }
}

/** Immutably write new text behind a finding path; returns an updated resume. */
export function setTextAtPath(resume: ResumeJSON, path: FindingPath, text: string): ResumeJSON {
  switch (path.kind) {
    case 'summary':
      return { ...resume, summary: text };
    case 'highlight':
      return { ...resume, highlights: (resume.highlights || []).map((h, i) => (i === path.index ? text : h)) };
    case 'summary_phrase':
      return { ...resume, summary_phrases: (resume.summary_phrases || []).map((p, i) => (i === path.index ? { ...p, text } : p)) };
    case 'skill':
      // Title only — tags, group_id and include flag ride along untouched.
      return { ...resume, skills: (resume.skills || []).map((s, i) => (i === path.index ? { ...s, title: text } : s)) };
    case 'companyDescription':
      // ONLY the description prose is written; company, url, dates, location,
      // employees, roles and the include flag all ride along untouched.
      return {
        ...resume,
        experience: (resume.experience || []).map((co, ci) => (ci === path.companyIndex ? { ...co, description: text } : co)),
      };
    case 'itemTitle': {
      // ONLY `title` is written. The original item is spread first, so tags,
      // url, year, org/issuer/reward/event and the include flag all survive
      // byte-identical — the section's own item type is preserved.
      const items = resume[path.section] as TitledItem[] | undefined;
      if (!items) return resume;
      return {
        ...resume,
        [path.section]: items.map((it, i) => (i === path.index ? { ...it, title: text } : it)),
      };
    }
    case 'bullet':
      return {
        ...resume,
        experience: (resume.experience || []).map((co, ci) => ci !== path.companyIndex ? co : {
          ...co,
          roles: (co.roles || []).map((role, ri) => ri !== path.roleIndex ? role : {
            ...role,
            bullets: (role.bullets || []).map((b, bi) => (bi === path.bulletIndex ? { ...b, text } : b)),
          }),
        }),
      };
  }
}

const wordCount = (s: string) => (s.trim().match(/\S+/g) || []).length;

// ── Authenticity: AI-tell detection ────────────────────────────────────────
// Weight by how strongly the term signals AI authorship. Ambiguous words that
// can be legitimate (robust, dynamic, elevate…) are 'low' so they nudge rather
// than dominate; hollow phrases that almost never appear in genuine human resume
// writing are 'high'.
const BANNED: { re: RegExp; label: string; severity: Severity }[] = [
  { re: /\bresults[-\s]driven\b/gi, label: '"results-driven"', severity: 'high' },
  { re: /\bproven track record\b/gi, label: '"proven track record"', severity: 'high' },
  { re: /\btrack record of\b/gi, label: '"track record of"', severity: 'med' },
  { re: /\bpassionate(ly)?\b/gi, label: '"passionate"', severity: 'med' },
  { re: /\bleverag(e|es|ed|ing)\b/gi, label: '"leverage"', severity: 'high' },
  { re: /\bspearhead(ed|ing|s)?\b/gi, label: '"spearheaded"', severity: 'high' },
  { re: /\bdemonstrated ability\b/gi, label: '"demonstrated ability"', severity: 'high' },
  { re: /\bdeep expertise\b/gi, label: '"deep expertise"', severity: 'med' },
  { re: /\bseamless(ly)?\b/gi, label: '"seamless"', severity: 'med' },
  { re: /\bcutting[-\s]edge\b/gi, label: '"cutting-edge"', severity: 'med' },
  { re: /\bsynerg(y|ies|istic)\b/gi, label: '"synergy"', severity: 'high' },
  { re: /\bin today'?s fast[-\s]paced\b/gi, label: '"in today\'s fast-paced…"', severity: 'high' },
  { re: /\bwheelhouse\b/gi, label: '"wheelhouse"', severity: 'med' },
  { re: /\btapestry\b/gi, label: '"tapestry"', severity: 'high' },
  { re: /\btestament to\b/gi, label: '"testament to"', severity: 'med' },
  { re: /\bnavigate the complexities\b/gi, label: '"navigate the complexities"', severity: 'high' },
  { re: /\bunleash(ing|ed|es)?\b/gi, label: '"unleash"', severity: 'med' },
  { re: /\bunlock(ing|ed|s)?\b/gi, label: '"unlock"', severity: 'low' },
  { re: /\belevat(e|ed|es|ing)\b/gi, label: '"elevate"', severity: 'low' },
  { re: /\brobust\b/gi, label: '"robust"', severity: 'low' },
  { re: /\bdynamic\b/gi, label: '"dynamic"', severity: 'low' },
];

const PENALTY: Record<Severity, number> = { high: 8, med: 5, low: 2 };

// Default rule-of-three tolerance: up to this fraction of resume items may be
// triple-lists before they count against the score. Overridable at runtime via
// the `rule_of_three_tolerance` global setting (see ScorecardModal).
export const DEFAULT_RULE_OF_THREE_TOLERANCE = 0.15;

const SEVERITY_ORDER: Record<Severity, number> = { high: 0, med: 1, low: 2 };

/**
 * Authenticity = the browser's own AI-tell scan PLUS the lint categories routed
 * here (style + credibility: trailing "etc", run-ons, repeated phrasing,
 * hyperbole, an overstated years claim). They are complementary, not
 * overlapping — the server's phrase lists (`_HYPERBOLE`) and the `BANNED` table
 * below share no entries — so a phrase is never penalized twice.
 *
 * Routed findings use the same LINT_PENALTY table as Accuracy: routing changes
 * WHICH dimension pays, not how much.
 */
function authenticityBuilder(
  snippets: Snippet[],
  tolerance: number,
  lint?: LintFinding[] | null,
  resume?: ResumeJSON,
): DimensionBuilder {
  const findings: Finding[] = [];

  // 1. Banned words / phrases
  for (const s of snippets) {
    for (const b of BANNED) {
      const m = s.text.match(b.re);
      if (m) {
        for (let i = 0; i < m.length; i++) {
          findings.push({ severity: b.severity, label: `AI-tell word ${b.label}`, section: s.section, path: s.path, penalty: PENALTY[b.severity] });
        }
      }
    }
  }

  // 2. "not only … but also"
  for (const s of snippets) {
    const m = s.text.match(/\bnot only\b[^.!?]*\bbut also\b/gi);
    if (m) m.forEach(() => { findings.push({ severity: 'med', label: '"not only … but also" construction', section: s.section, path: s.path, penalty: 6 }); });
  }

  // 3. Rule-of-three: the AI "tricolon" tell — exactly THREE SINGLE-WORD items
  //    in a serial list ("fast, reliable, and scalable" / "designed, built, and
  //    launched"). Deliberately narrow: the classic tell is short, parallel,
  //    single-word items used for rhetorical rhythm. We do NOT flag:
  //      • multi-word enumerations ("panel discussions, hackathon workshops, and
  //        conference presentations") — the single-token items in the pattern
  //        below can't match phrases with spaces;
  //      • lists of four or more items — a leading "word," before the match
  //        means it's the tail of a longer list, so it's skipped;
  //      • compound sentences whose comma-clauses aren't single words.
  //    Oxford comma optional (",? and"). A tolerance band still applies: the
  //    first `tolerance` fraction of resume items may be tricolons for FREE,
  //    only the excess is penalized. Every instance is still flagged so the
  //    user sees it — only the SCORE is forgiven. `tolerance` is a tweakable
  //    global setting (rule_of_three_tolerance).
  const TRICOLON = /([\w'-]+),\s+([\w'-]+),?\s+(?:and|or)\s+([\w'-]+)/gi;
  const ruleOfThreeFindings: Finding[] = [];
  for (const s of snippets) {
    s.text.split(/(?<=[.!?])\s+/).forEach((sent) => {
      TRICOLON.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = TRICOLON.exec(sent)) !== null) {
        // Skip the tail of a 4+ item list: a preceding "word," means an earlier item.
        if (/[\w'-]+,\s*$/.test(sent.slice(0, m.index))) continue;
        ruleOfThreeFindings.push({ severity: 'low', label: 'Rule-of-three list ("X, Y, and Z")', section: s.section, detail: m[0], path: s.path, group: 'rule_of_three', penalty: 4 });
      }
    });
  }
  findings.push(...ruleOfThreeFindings);

  // 4. Em-dash clause splices
  for (const s of snippets) {
    const m = s.text.match(/\s[—–]\s|\s-\s/g);
    if (m && m.length) findings.push({ severity: 'low', label: `Em-dash splice ×${m.length}`, section: s.section, path: s.path, penalty: 2 * m.length });
  }

  // 5. Participle-opening density across bullets ("Leading…", "Driving…")
  const bullets = snippets.filter((s) => s.isBullet);
  if (bullets.length >= 3) {
    const ing = bullets.filter((b) => /^\s*[A-Z][a-z]+ing\b/.test(b.text));
    const ratio = ing.length / bullets.length;
    if (ratio > 0.35) {
      findings.push({ severity: 'med', label: `${Math.round(ratio * 100)}% of bullets open with an "-ing" participle (monotone rhythm)`, section: 'Experience / Highlights', penalty: Math.round((ratio - 0.35) * 60) });
    }
  }

  // 6. Server lint findings routed here by category (style / credibility).
  findings.push(...mapLintFindings(lintFor(lint, 'authenticity'), resume));

  // Free allowance scales with resume size (snippet count) so the tolerance is
  // relative, not absolute — consistent with the length-invariant scoring model.
  const freeAllowance = Math.floor(tolerance * snippets.length);
  const totalWords = snippets.reduce((n, s) => n + wordCount(s.text), 0);

  // Sort findings high→low so the worst show first.
  findings.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  return {
    key: 'authenticity',
    label: 'Authenticity',
    findings,
    finalize(kept) {
      let penalty = 0;
      let ruleOfThree = 0;
      for (const f of kept) {
        if (f.group === 'rule_of_three') ruleOfThree++;
        else penalty += f.penalty || 0;
      }
      penalty += Math.max(0, ruleOfThree - freeAllowance) * 4;

      // Relative scoring: score on penalty DENSITY (points per 100 words), not
      // the absolute penalty, so a longer resume isn't punished for length —
      // only for its RATE of AI-tells. Two resumes with the same density score
      // the same regardless of how many bullets they have.
      //
      // Smooth exponential decay (never a hard floor at 0):
      // score = 100·e^(−density/K). Starts at 100 with zero tells and falls off
      // as density rises, but always moves when you clean things up — unlike the
      // old `max(0, 100 − penalty)`, which pinned heavy resumes at 0 so real
      // fixes produced no visible change. K calibrated against real resumes:
      // clean ≈ 95, repetitive ≈ 45, AI-slop ≈ 0. Denominator floored at 150
      // words so a tiny resume with one tell doesn't crater.
      const AUTH_DECAY_SCALE = 9;
      const density = (penalty / Math.max(totalWords, 150)) * 100;
      const score = totalWords === 0 ? null : Math.round(100 * Math.exp(-density / AUTH_DECAY_SCALE));

      let blurb: string;
      if (score === null) blurb = 'No written content yet to evaluate.';
      else if (score >= 90) blurb = 'Reads human and specific — very few AI tells.';
      else if (score >= 75) blurb = 'Mostly natural; a handful of AI tells worth trimming.';
      else if (score >= 50) blurb = 'Several AI-sounding patterns — worth a editing pass.';
      else blurb = 'Reads strongly AI-generated; recruiters may notice.';
      return { score, blurb };
    },
  };
}

// ── Accuracy (key 'correctness'): deterministic lint findings from the server ─
// The lint engine is server-side (shared/resume_lint.py) because the wordlist
// and rule set are shared with the AI review pipeline. The scorecard turns its
// findings into a dimension without ever changing the resume — every finding is
// informational here; applying a fix is the Review/recommendation flow's job.
//
// NOTE: the dimension KEY stays 'correctness' forever — it is baked into every
// finding id and therefore into users' persisted "resolved" lists. Only the
// user-facing LABEL is "Accuracy".
export const CORRECTNESS_LABEL = 'Accuracy';

/**
 * Client-side severity policy (owner decision 2026-07-26): a suspected TYPO
 * ("Netfilx" for "Netflix") or GRAMMATICAL mistake (article disagreement,
 * subject/person shift, tense drift) is ALWAYS 'high' — regardless of the
 * severity the server assigned. Calibration: prefer false positives; the user
 * can "Mark resolved" to dismiss one forever, and missing an obvious typo on a
 * resume is far more costly than over-flagging.
 *
 * Derived from the rule table in `infrastructure/lambdas/shared/resume_lint.py`.
 * Stylistic / duplication / credibility rules keep their server severity.
 */
export const HIGH_SEVERITY_LINT_RULES: Record<string, 'typo' | 'grammar'> = {
  doubled_word: 'grammar',        // server: high     — "revenue revenue"
  spelling: 'typo',               // server: med      — "managment"
  proper_noun_variant: 'typo',    // server: med      — "Netfilx" vs "Netflix"
  word_pair_typo: 'typo',         // server: med      — "affect"/"effect"
  garbage_chars: 'typo',          // server: high     — corrupt glyphs / "a&b"
  article_agreement: 'grammar',   // server: med      — "a engineer"
  tense_drift: 'grammar',         // server: med      — present tense in an ended role
  person_shift: 'grammar',        // server: low      — third-person phrasing
};

/**
 * The severity the SCORECARD uses for a lint finding. Three tiers of matching so
 * a rule added server-side later still inherits the policy without a webapp
 * deploy: explicit rule id → rule-name shape → the server's own 'correctness'
 * category (which is exactly the spelling/grammar family).
 */
export function effectiveSeverity(f: LintFinding): Severity {
  if (HIGH_SEVERITY_LINT_RULES[f.rule]) return 'high';
  if (/spell|typo|grammar|tense|article|agreement|doubl|plural|conjugat/i.test(f.rule)) return 'high';
  if (f.category === 'correctness') return 'high';
  return f.severity;
}

/**
 * Which dimension a server lint finding belongs to, keyed on its `category`
 * (the single routing key — see the module header and the category table in
 * `infrastructure/lambdas/shared/resume_lint.py`).
 *
 * This is INDEPENDENT of severity: `effectiveSeverity` may promote a finding to
 * 'high', which decides what it COSTS, never where it LANDS.
 */
export const LINT_CATEGORY_DIMENSION: Record<string, DimensionScore['key']> = {
  correctness: 'correctness',   // spelling, tense drift, doubled words…
  consistency: 'correctness',   // duplicate skills, mixed notation, missing years
  completeness: 'completeness', // too few skills…
  style: 'authenticity',        // trailing "etc", run-ons, repeated phrasing
  credibility: 'authenticity',  // hyperbole, overstated years claim
};

/**
 * A category this build has never seen falls back to Accuracy — the dimension
 * that ingested EVERYTHING before routing existed, so an unknown category can
 * never make a finding disappear from the scorecard.
 */
export const DEFAULT_LINT_DIMENSION: DimensionScore['key'] = 'correctness';

export function lintDimension(f: LintFinding): DimensionScore['key'] {
  return LINT_CATEGORY_DIMENSION[(f.category || '').trim().toLowerCase()] || DEFAULT_LINT_DIMENSION;
}

/** The lint findings routed to one dimension (`null` stays `null` = loading). */
function lintFor(findings: LintFinding[] | null | undefined, key: DimensionScore['key']): LintFinding[] {
  return (findings || []).filter((f) => lintDimension(f) === key);
}

// Only HIGH and MED findings move the score. LOW findings (unexplained
// acronyms, run-on sentences, hyperbole…) are LISTED so the user sees them but
// score ZERO — on a real resume they outnumber the genuine errors and would
// otherwise dominate a dimension that's meant to measure actual mistakes.
// Deliberately the SAME table wherever a lint finding lands, so routing changes
// which dimension pays the penalty, never how big it is.
const LINT_PENALTY: Record<Severity, number> = { high: 8, med: 5, low: 0 };

// Same length-invariant model as authenticity: penalty density per 100 words
// with a 150-word floor, smooth exponential decay so cleaning up always moves
// the number. Tighter scale (6 vs 9) — outright errors cost more than AI tells.
const CORRECTNESS_DECAY_SCALE = 6;

const SECTION_LABELS: Record<string, string> = {
  skills: 'Skills',
  education: 'Education',
  awards: 'Awards',
  presentations: 'Presentations',
  publications: 'Publications',
  portfolios: 'Portfolios',
  summary_phrases: 'Summary phrase',
  highlights: 'Highlights',
};

const BULLET_PATH = /^experience\[(\d+)\]\.roles\[(\d+)\]\.bullets\[(\d+)\]$/;
const ROLE_PATH = /^experience\[(\d+)\]\.roles\[(\d+)\]$/;
const COMPANY_PATH = /^experience\[(\d+)\]$/;
const COMPANY_DESC_PATH = /^experience\[(\d+)\]\.description$/;
const ITEM_TITLE_PATH = new RegExp(`^(${ITEM_TITLE_SECTIONS.join('|')})\\[(\\d+)\\]\\.title$`);
const INDEXED_PATH = /^([a-z_]+)\[(\d+)\]$/;
/** Field suffixes the client grammar appends (see `lintFindingPath`). */
const FIELD_SUFFIX_RE = /\.(?:description|title)$/;

/**
 * Canonical lint path for a server finding — the ONE place `field` is folded
 * into the path grammar.
 *
 * The linter addresses a company entry's NAME and its DESCRIPTION with the SAME
 * path (`experience[2]`), telling them apart only by `field` ('company' vs
 * 'description'). The scorecard's own path grammar is field-free — it flows
 * through `findingLintPaths` → `FindingEntry` as a bare string — so a
 * description finding used to collapse onto the company anchor: the row was
 * breadcrumbed with the company NAME and offered no editor, and the flagged
 * prose never appeared (owner report 2026-07-27, hyperbole "tamperproof" in the
 * MakeInfinite Labs description). The backend shape is CORRECT; this extends
 * the client grammar with `experience[i].description` so the description gets
 * its own addressable, editable anchor.
 *
 * The same problem applies to the TITLED LIST sections, which pack several
 * linted fields onto ONE path — `awards[0]` addresses title AND issuer AND
 * reward — so `<section>[i]` + field 'title' becomes `<section>[i].title`.
 * Pinning the field in the path is what makes title editing SAFE: without it a
 * finding on an award's `issuer` would open an editor loaded with the TITLE and
 * its Save would overwrite the wrong field.
 *
 * DELIBERATELY NARROW — everything else is returned untouched:
 *   • `experience[2]`+'company'          → company NAMES stay read-only
 *   • `experience[2].roles[0]`+'title'   → role titles stay read-only
 *   • `awards[0]`+'issuer' / 'reward', `education[1]`+'org', …
 *                                        → non-title fields stay read-only
 *   • `skills[3]`+'title'                → already the `skill` kind, which is
 *     ALSO delete-capable; its bare `skills[i]` grammar is load-bearing for the
 *     merge flow's deletion remapping and must not gain a suffix.
 */
export function lintFindingPath(path: string, field?: string): string {
  if (field === 'description' && COMPANY_PATH.test(path)) return `${path}.description`;
  if (field === 'title') {
    const indexed = INDEXED_PATH.exec(path);
    if (indexed && (ITEM_TITLE_SECTIONS as readonly string[]).includes(indexed[1])) {
      return `${path}.title`;
    }
  }
  return path;
}

/**
 * Map a lint path onto an editable FindingPath, when it addresses one of the
 * item kinds the scorecard can edit in place (summary, highlight, summary
 * phrase, SKILL title, experience bullet). Anything else (education titles,
 * company names, resume-wide findings) stays read-only.
 */
export function lintPathToFindingPath(path: string): FindingPath | undefined {
  if (path === 'summary') return { kind: 'summary' };
  const desc = COMPANY_DESC_PATH.exec(path);
  if (desc) return { kind: 'companyDescription', companyIndex: Number(desc[1]) };
  const title = ITEM_TITLE_PATH.exec(path);
  if (title) {
    return { kind: 'itemTitle', section: title[1] as ItemTitleSection, index: Number(title[2]) };
  }
  const bullet = BULLET_PATH.exec(path);
  if (bullet) {
    return {
      kind: 'bullet',
      companyIndex: Number(bullet[1]),
      roleIndex: Number(bullet[2]),
      bulletIndex: Number(bullet[3]),
    };
  }
  const indexed = INDEXED_PATH.exec(path);
  if (indexed) {
    const [, key, idx] = indexed;
    if (key === 'highlights') return { kind: 'highlight', index: Number(idx) };
    if (key === 'summary_phrases') return { kind: 'summary_phrase', index: Number(idx) };
    // `skills[3]` — the item a multi-item duplicate-skill finding points at.
    // (The BARE `skills` path some findings carry addresses the whole section
    // and stays read-only; its `related` list supplies the indexed items.)
    if (key === 'skills') return { kind: 'skill', index: Number(idx) };
  }
  return undefined;
}

/** The lint path grammar for a FindingPath (inverse of lintPathToFindingPath). */
export function findingPathToLintPath(path: FindingPath): string {
  switch (path.kind) {
    case 'summary': return 'summary';
    case 'highlight': return `highlights[${path.index}]`;
    case 'summary_phrase': return `summary_phrases[${path.index}]`;
    case 'skill': return `skills[${path.index}]`;
    case 'companyDescription': return `experience[${path.companyIndex}].description`;
    case 'itemTitle': return `${path.section}[${path.index}].title`;
    case 'bullet': return `experience[${path.companyIndex}].roles[${path.roleIndex}].bullets[${path.bulletIndex}]`;
  }
}

// ── Deleting an item, and the index shift it causes ─────────────────────────
//
// The Scorecard's merge flow ("these two skills are the same — keep one") has
// to DELETE a resume item while a frozen list of findings, each carrying
// `skills[i]` / `experience[c].roles[r].bullets[b]` paths, is on screen.
// Splicing the array renumbers every later item, so a path captured before the
// delete would then address the WRONG one — a pending edit could silently
// overwrite an unrelated skill or bullet.
//
// Everything below is pure, so the (fiddly) index math is unit-tested rather
// than trusted: the modal keeps an ordered LOG of the deletions it has applied
// and re-derives every frozen path through `remapLintPath` at render time.
//
// TWO deletable kinds, and they never interfere:
//   • a SKILL deletion renumbers only `skills[i]` paths;
//   • a BULLET deletion renumbers only bullets in the SAME company+role
//     (company and role indices are untouched by it).

/**
 * One applied SKILL deletion, recorded in the array space that existed at the
 * time. `kind` is optional and defaults to 'skill' so every record and caller
 * that predates bullet deletion stays valid, byte for byte.
 */
export interface SkillDeletion {
  kind?: 'skill';
  /** Index in the live `skills` array at the moment it was deleted. */
  index: number;
  /** The removed item, kept so the deletion can be undone in-session. */
  item: Skill;
}

/** Where one experience bullet lives — the three indices that address it. */
export interface BulletLocation {
  companyIndex: number;
  roleIndex: number;
  bulletIndex: number;
}

/** One applied BULLET deletion, in the array space that existed at the time. */
export interface BulletDeletion extends BulletLocation {
  kind: 'bullet';
  /** The removed bullet (text + tags + include flag), kept for the Undo. */
  item: ExperienceBullet;
}

/** Either kind of in-session deletion the Scorecard can apply and undo. */
export type ScorecardDeletion = SkillDeletion | BulletDeletion;

export function isBulletDeletion(d: ScorecardDeletion): d is BulletDeletion {
  return d.kind === 'bullet';
}

/** The kind of delete an expanded finding entry can offer, or null for none. */
export function entryDeleteKind(path?: FindingPath): 'skill' | 'bullet' | null {
  if (!path) return null;
  if (path.kind === 'skill') return 'skill';
  if (path.kind === 'bullet') return 'bullet';
  return null;
}

/** Remove `skills[index]`, leaving every other section untouched. */
export function deleteSkillAt(resume: ResumeJSON, index: number): ResumeJSON {
  const skills = resume.skills || [];
  if (index < 0 || index >= skills.length) return resume;
  return { ...resume, skills: skills.filter((_, i) => i !== index) };
}

/** Put a removed skill back at `index` (the inverse of `deleteSkillAt`). */
export function insertSkillAt(resume: ResumeJSON, index: number, item: Skill): ResumeJSON {
  const skills = [...(resume.skills || [])];
  skills.splice(Math.max(0, Math.min(index, skills.length)), 0, item);
  return { ...resume, skills };
}

/** The bullet at a location, or undefined when the indices don't resolve. */
export function getBulletAt(resume: ResumeJSON, at: BulletLocation): ExperienceBullet | undefined {
  return resume.experience?.[at.companyIndex]?.roles?.[at.roleIndex]?.bullets?.[at.bulletIndex];
}

/**
 * Immutably rewrite ONE role's bullet list. Company, role, dates, tags and every
 * other bullet ride along untouched — only the addressed list is replaced.
 */
function withBullets(
  resume: ResumeJSON,
  at: BulletLocation,
  fn: (bullets: ExperienceBullet[]) => ExperienceBullet[],
): ResumeJSON {
  return {
    ...resume,
    experience: (resume.experience || []).map((co, ci) => (ci !== at.companyIndex ? co : {
      ...co,
      roles: (co.roles || []).map((role, ri) => (ri !== at.roleIndex ? role : {
        ...role,
        bullets: fn(role.bullets || []),
      })),
    })),
  };
}

/**
 * Remove one experience bullet. Out-of-range is a no-op (returns the SAME
 * object) rather than a corruption.
 *
 * Emptying a role's bullet list is ALLOWED: the editor's own bullet remove does
 * the same, and the save-side validator only requires `bullets` to be a list
 * (`shared/validation.py`) — an empty one is valid.
 */
export function deleteBulletAt(resume: ResumeJSON, at: BulletLocation): ResumeJSON {
  const bullets = resume.experience?.[at.companyIndex]?.roles?.[at.roleIndex]?.bullets;
  if (!bullets || at.bulletIndex < 0 || at.bulletIndex >= bullets.length) return resume;
  return withBullets(resume, at, (bs) => bs.filter((_, bi) => bi !== at.bulletIndex));
}

/** Put a removed bullet back where it was (the inverse of `deleteBulletAt`). */
export function insertBulletAt(resume: ResumeJSON, at: BulletLocation, item: ExperienceBullet): ResumeJSON {
  const role = resume.experience?.[at.companyIndex]?.roles?.[at.roleIndex];
  if (!role) return resume;
  return withBullets(resume, at, (bs) => {
    const next = [...bs];
    next.splice(Math.max(0, Math.min(at.bulletIndex, next.length)), 0, item);
    return next;
  });
}

const SKILL_PATH = /^skills\[(\d+)\]$/;

/**
 * Re-address a FROZEN lint path against the live resume after a sequence of
 * deletions.
 *
 * Returns `null` when the path IS one of the deleted items (the block renders
 * as removed), the SAME string when it is unaffected (any path neither kind of
 * deletion can move — summary, education, a bullet in another role — is
 * returned untouched), and a renumbered path otherwise.
 *
 * The log is applied IN ORDER: entry n was recorded in the array space left by
 * entries 0…n-1, which is exactly the order a sequential replay reproduces.
 */
export function remapLintPath(path: string, deletions: ScorecardDeletion[]): string | null {
  return resolveLintPath(path, deletions).path;
}

/**
 * `remapLintPath` plus WHICH log entry consumed the path (`deletedBy`), so a
 * removed block can offer an Undo that targets the right deletion.
 */
export function resolveLintPath(
  path: string,
  deletions: ScorecardDeletion[],
): { path: string | null; deletedBy: number | null } {
  const skill = SKILL_PATH.exec(path);
  if (skill) {
    let idx = Number(skill[1]);
    for (let i = 0; i < deletions.length; i++) {
      const d = deletions[i];
      if (isBulletDeletion(d)) continue;   // can't move a skill
      if (idx === d.index) return { path: null, deletedBy: i };
      if (idx > d.index) idx -= 1;
    }
    return { path: `skills[${idx}]`, deletedBy: null };
  }
  const bullet = BULLET_PATH.exec(path);
  if (bullet) {
    const companyIndex = Number(bullet[1]);
    const roleIndex = Number(bullet[2]);
    let idx = Number(bullet[3]);
    for (let i = 0; i < deletions.length; i++) {
      const d = deletions[i];
      // Only a deletion in the SAME role can renumber this bullet.
      if (!isBulletDeletion(d) || d.companyIndex !== companyIndex || d.roleIndex !== roleIndex) continue;
      if (idx === d.bulletIndex) return { path: null, deletedBy: i };
      if (idx > d.bulletIndex) idx -= 1;
    }
    return { path: `experience[${companyIndex}].roles[${roleIndex}].bullets[${idx}]`, deletedBy: null };
  }
  return { path, deletedBy: null };
}

/**
 * The deletion log after UNDOING entry `undoIndex`.
 *
 * Re-inserting that item pushes every live item at or after its position back
 * up by one, so any LATER entry OF THE SAME KIND (recorded in the space where it
 * was already gone) that sits at or after the restored position must be
 * renumbered +1 — otherwise a subsequent replay would address the wrong item.
 * Entries recorded BEFORE it are in an older space and are untouched, and so is
 * every entry the restore cannot move (the other kind, or a bullet in another
 * role).
 *
 * Generic in the log's element type so a skills-only caller still gets
 * `SkillDeletion[]` back, exactly as before.
 */
export function logAfterUndo<T extends ScorecardDeletion>(log: T[], undoIndex: number): T[] {
  const target = log[undoIndex];
  if (!target) return log;
  return log
    .filter((_, i) => i !== undoIndex)
    .map((d, i) => {
      if (i < undoIndex) return d;
      if (isBulletDeletion(target)) {
        return isBulletDeletion(d)
          && d.companyIndex === target.companyIndex
          && d.roleIndex === target.roleIndex
          && d.bulletIndex >= target.bulletIndex
          ? { ...d, bulletIndex: d.bulletIndex + 1 } as T
          : d;
      }
      return !isBulletDeletion(d) && d.index >= target.index
        ? { ...d, index: d.index + 1 } as T
        : d;
    });
}

/**
 * Locator for a REMOVED bullet's struck-through block. The company and role
 * indices are untouched by a bullet deletion, so they still resolve against the
 * live resume; the bullet's own text comes from the log (it is gone from the
 * resume), which is what keeps the block readable after the removal.
 */
export function bulletDeletionLabel(d: BulletDeletion, resume?: ResumeJSON): string {
  const where = humanizePath(`experience[${d.companyIndex}].roles[${d.roleIndex}]`, resume);
  const text = (d.item?.text || '').trim();
  return text ? `${where} › ${text}` : where;
}

/** Human-readable section name for a lint path ('Experience — Acme Inc.'). */
export function lintPathToSection(pathIn: string, resume?: ResumeJSON): string {
  // Accepts either the RAW server path or the client's field-suffixed grammar
  // (`awards[0].title`) — the section is the same either way.
  const path = pathIn.replace(FIELD_SUFFIX_RE, '');
  if (!path) return 'Resume';
  if (path === 'summary') return 'Summary';
  if (path === 'skills') return 'Skills';
  // Bare section anchor, used by whole-section structural findings
  // (concurrent_present_roles). Without this the row was chipped "Resume" while
  // its own note said "your Experience section".
  if (path === 'experience') return 'Experience';
  const bullet = BULLET_PATH.exec(path);
  const company = bullet ? Number(bullet[1]) : (/^experience\[(\d+)\]/.exec(path) ? Number(/^experience\[(\d+)\]/.exec(path)![1]) : null);
  if (company !== null) {
    const name = resume?.experience?.[company]?.company;
    return name ? `Experience — ${name}` : 'Experience';
  }
  const indexed = INDEXED_PATH.exec(path);
  const key = indexed ? indexed[1] : path;
  return SECTION_LABELS[key] || 'Resume';
}

/**
 * Top-level resume section a finding belongs to, for the chip that leads every
 * Scorecard row (owner request 2026-07-27: a duplicate-SKILL finding was read
 * as an experience-bullet problem because the only section cue was the trailing
 * gray breadcrumb). Derived from the finding's existing `section` string, so
 * there is exactly one source of truth:
 *   'Experience — Acme Inc.' → 'Experience'   (company suffix dropped)
 *   'Summary phrase'         → 'Summary'      (sub-kind folded into its section)
 *   'Experience / Highlights'→ 'Experience'   (cross-section rhythm check)
 *   '' / anything unknown    → 'Resume'       (resume-wide finding)
 * The breadcrumb is untouched: the chip is the at-a-glance answer, the
 * breadcrumb still carries the specifics.
 */
const CHIP_SECTIONS = [
  'Experience', 'Skills', 'Summary', 'Education', 'Awards',
  'Presentations', 'Publications', 'Portfolios', 'Contact', 'Highlights',
] as const;

export type FindingSection = (typeof CHIP_SECTIONS)[number] | 'Resume';

export function findingSectionChip(section?: string | null): FindingSection {
  const head = (section || '').split(/\s+—\s+|\s+\/\s+/)[0].trim();
  if (!head) return 'Resume';
  return CHIP_SECTIONS.find((s) => head === s || head.startsWith(`${s} `)) || 'Resume';
}

// ── Humanized addressing (no raw index paths in the UI) ─────────────────────
// Every path the linter emits is machine grammar (`experience[0].roles[0]
// .bullets[5]`). Users see NAMES and 1-BASED counting instead:
//   `Acme Inc. › Head of Solutions › bullet 6`.
// Anything that can't be resolved (renamed company, out-of-range index) falls
// back to the raw path rather than throwing or inventing a label.

/** Matches one path-grammar token anywhere in a string (longest form first). */
const PATH_TOKEN_RE =
  /experience\[\d+\](?:\.description|\.roles\[\d+\](?:\.bullets\[\d+\])?)?|[a-z_]+\[\d+\](?:\.title)?/g;

/** Titled list sections that address items by index (`education[1]`). */
const TITLED_LISTS: Record<string, string> = {
  skills: 'Skills',
  education: 'Education',
  awards: 'Awards',
  presentations: 'Presentations',
  publications: 'Publications',
  portfolios: 'Portfolios',
};

/**
 * Turn a lint path into a human locator using the resume's own names and
 * 1-based counting. Falls back to the raw path when the target can't be found.
 */
export function humanizePath(path: string, resume?: ResumeJSON): string {
  if (!path) return 'Resume';
  if (path === 'summary') return 'Summary';
  if (path === 'experience') return 'Experience';
  if (SECTION_LABELS[path]) return SECTION_LABELS[path];

  // The company blurb, named as its own field so the row never reads as if the
  // company NAME were the thing to edit.
  const desc = COMPANY_DESC_PATH.exec(path);
  if (desc) {
    const co = resume?.experience?.[Number(desc[1])];
    if (!co) return path;
    return `${co.company?.trim() || `Experience entry ${Number(desc[1]) + 1}`} › company description`;
  }

  // A titled-list item TITLE reads exactly like the item itself — the title IS
  // the item's name — so it reuses the `<section>[i]` locator.
  const itemTitle = ITEM_TITLE_PATH.exec(path);
  if (itemTitle) {
    const base = `${itemTitle[1]}[${itemTitle[2]}]`;
    const human = humanizePath(base, resume);
    return human === base ? path : human;
  }

  const bullet = BULLET_PATH.exec(path);
  if (bullet) {
    const co = resume?.experience?.[Number(bullet[1])];
    const role = co?.roles?.[Number(bullet[2])];
    const bi = Number(bullet[3]);
    const text = role?.bullets?.[bi]?.text;
    if (!co || !role || text === undefined) return path;
    const company = co.company?.trim() || 'Experience';
    const title = role.title?.trim() || `role ${Number(bullet[2]) + 1}`;
    return `${company} › ${title} › bullet ${bi + 1}`;
  }

  const role = ROLE_PATH.exec(path);
  if (role) {
    const co = resume?.experience?.[Number(role[1])];
    const r = co?.roles?.[Number(role[2])];
    if (!co || !r) return path;
    return `${co.company?.trim() || 'Experience'} › ${r.title?.trim() || `role ${Number(role[2]) + 1}`}`;
  }

  const company = COMPANY_PATH.exec(path);
  if (company) {
    const co = resume?.experience?.[Number(company[1])];
    if (!co) return path;
    return co.company?.trim() || `Experience entry ${Number(company[1]) + 1}`;
  }

  const indexed = INDEXED_PATH.exec(path);
  if (indexed) {
    const key = indexed[1];
    const i = Number(indexed[2]);
    if (key === 'highlights') {
      const items = resume?.highlights;
      if (items && i >= items.length) return path;
      return `Highlights › item ${i + 1}`;
    }
    if (key === 'summary_phrases') {
      const items = resume?.summary_phrases;
      if (items && i >= items.length) return path;
      return `Summary phrase ${i + 1}`;
    }
    const label = TITLED_LISTS[key];
    if (label) {
      const items = (resume as unknown as Record<string, { title?: string }[]> | undefined)?.[key];
      if (!items || i >= items.length) return path;
      const title = items[i]?.title?.trim();
      return `${label} › ${title || `item ${i + 1}`}`;
    }
  }
  return path;
}

/**
 * Rewrite every path-grammar token INSIDE a message string into its human form.
 * Server messages embed paths (e.g. repeated_ngram lists the bullets it found),
 * and users must never see `experience[0].roles[0].bullets[5]`.
 */
export function humanizeMessage(message: string, resume?: ResumeJSON): string {
  if (!message) return message;
  return message.replace(PATH_TOKEN_RE, (tok) => humanizePath(tok, resume));
}

/**
 * Every distinct path referenced by a finding, in the order the user should see
 * them. A MULTI-item finding is authoritative about its own membership — the
 * server's `related` list already includes the item `path` points at, in resume
 * order — so it wins outright. Otherwise: own path first, then any embedded in
 * the message (repeated phrasing names the bullets it matched).
 */
export function findingLintPaths(finding: Finding): string[] {
  const out: string[] = [];
  if (finding.related?.length) {
    for (const r of finding.related) if (r.path && !out.includes(r.path)) out.push(r.path);
    if (out.length) return out;
  }
  const own = finding.rawPath || (finding.path ? findingPathToLintPath(finding.path) : '');
  if (own) out.push(own);
  const inMessage = finding.label.match(PATH_TOKEN_RE) || [];
  for (const p of inMessage) if (!out.includes(p)) out.push(p);
  return out;
}

/**
 * How an EXPANDED finding row should present itself. Chosen from what the
 * finding actually carries — never from its rule id — so a new server rule gets
 * the right treatment for free:
 *
 *   'editable'   — at least one path the modal can edit in place (summary,
 *                  bullet, highlight, summary phrase, skill title).
 *   'related'    — a multi-item finding: one block per involved item.
 *   'reference'  — anchors ONE specific item that the modal can't edit
 *                  (`education[3]`, an award, a role title): name where it
 *                  lives, as text — not as a form control.
 *   'structural' — no item anchor at all, or an anchor that is really a WHOLE
 *                  section (`experience`, `awards`): nothing to edit, so the row
 *                  shows no editor. Owner 2026-07-27: the old unlabeled
 *                  "Edit this item in its resume section" box read as an editor
 *                  for nothing ("I have no idea what this would even edit").
 */
export type FindingRenderTier = 'editable' | 'related' | 'reference' | 'structural';

/** True when a lint path addresses ONE item, not a whole section / the resume. */
export function isItemAnchor(path: string): boolean {
  if (!path) return false;
  return BULLET_PATH.test(path) || ROLE_PATH.test(path) || COMPANY_DESC_PATH.test(path)
    || ITEM_TITLE_PATH.test(path) || COMPANY_PATH.test(path) || INDEXED_PATH.test(path);
}

export function findingRenderTier(finding: Finding): FindingRenderTier {
  if (finding.related && finding.related.length > 1) return 'related';
  const paths = findingLintPaths(finding);
  if (paths.some((p) => !!lintPathToFindingPath(p))) return 'editable';
  if (paths.some(isItemAnchor)) return 'reference';
  return 'structural';
}

// ── My Dictionary: teaching the spell-checker a real word ───────────────────
//
// The linter's wordlist can't know a user's industry jargon, product names or
// surname, so `spelling` findings are the noisiest thing in the Accuracy list.
// "Add to My Dictionary" writes the word into the user's `custom_dictionary`
// preference; the lint endpoint reads it (from the request body, and falling
// back to stored preferences) and stops flagging it — everywhere, for every
// resume, permanently. Everything below is PURE so the fiddly parts (which word,
// which findings go away, the cap) are unit-tested rather than trusted.

/** Preference key holding the user's personal wordlist (lowercase words). */
export const CUSTOM_DICTIONARY_KEY = 'custom_dictionary';

/** Hard cap on the stored wordlist — a bounded preference, not a corpus. */
export const CUSTOM_DICTIONARY_MAX = 500;

/**
 * A plausible dictionary word: letters, with internal hyphens/apostrophes
 * allowed ("web3" is out, "e-commerce" and "O'Brien" are in). Unicode-aware so
 * non-ASCII names qualify. Anything else (a phrase, a number, punctuation) is
 * rejected rather than written into the user's preferences.
 */
const DICTIONARY_WORD_RE = /^\p{L}(?:[\p{L}'’-]*\p{L})?$/u;

/** First double-quoted token in a message — `Possible misspelling: "Netfilx"`. */
const QUOTED_TOKEN_RE = /["“”]([^"“”]+)["“”]/;

/**
 * The word a SPELLING finding is about, or null when this isn't one (or the
 * token doesn't look like a word).
 *
 * Prefers the server's structured `word` field; falls back to the quoted token
 * in the message, so this works both before and after that field ships.
 */
export function spellingWordOf(
  input: { rule?: string; message?: string; word?: string | null },
): string | null {
  if (input.rule !== 'spelling') return null;
  const raw = (input.word || QUOTED_TOKEN_RE.exec(input.message || '')?.[1] || '').trim();
  if (!raw || !DICTIONARY_WORD_RE.test(raw)) return null;
  return raw.toLowerCase();
}

/** The word behind a scorecard Finding (its `label` IS the server message). */
export function findingSpellingWord(f: Finding): string | null {
  return spellingWordOf({ rule: f.rule, message: f.label, word: f.word });
}

/** The word behind a raw server LintFinding. */
export function lintSpellingWord(f: LintFinding): string | null {
  return spellingWordOf({ rule: f.rule, message: f.message, word: f.word });
}

/**
 * Every spelling finding for `word` removed — ALL of them, not just the row the
 * user clicked. One word is usually flagged in several places (a product name
 * in the summary AND three bullets); teaching the dictionary settles all of
 * them at once, so leaving the others listed would read as a broken action.
 */
export function dropSpellingFindings(findings: LintFinding[], word: string): LintFinding[] {
  const target = word.trim().toLowerCase();
  if (!target) return findings;
  return findings.filter((f) => lintSpellingWord(f) !== target);
}

/**
 * Every spelling finding covered by the user's dictionary removed.
 *
 * Applied to EVERY lint result the modal handles, not just the one right after
 * an "Add to My Dictionary" click. The dictionary is the user's stated truth,
 * so the client enforces it locally rather than trusting a round trip: that
 * makes the button work the instant it is pressed (no waiting on a re-lint), it
 * survives a lint request that fails, and it stays correct when the server also
 * filters — the drop simply becomes a no-op.
 */
export function applyDictionary(findings: LintFinding[], dictionary: string[] | undefined | null): LintFinding[] {
  const known = new Set((dictionary || []).map((w) => String(w || '').trim().toLowerCase()).filter(Boolean));
  if (known.size === 0) return findings;
  return findings.filter((f) => {
    const word = lintSpellingWord(f);
    return !word || !known.has(word);
  });
}

/**
 * The dictionary after adding `word`, or null when there is nothing to write
 * (not a word, or already known — case-insensitively). Newest entries win the
 * cap: at `CUSTOM_DICTIONARY_MAX` the oldest are dropped, so adding a word can
 * never fail loudly on a long-lived account.
 */
export function addToDictionary(current: string[] | undefined | null, word: string): string[] | null {
  const clean = (word || '').trim().toLowerCase();
  if (!clean || !DICTIONARY_WORD_RE.test(clean)) return null;
  const existing = (current || [])
    .map((w) => String(w || '').trim().toLowerCase())
    .filter(Boolean);
  if (existing.includes(clean)) return null;
  const next = [...existing, clean];
  // Dedupe defensively (a stored list may predate this normalization).
  const deduped = Array.from(new Set(next));
  return deduped.slice(Math.max(0, deduped.length - CUSTOM_DICTIONARY_MAX));
}

/**
 * Whether a finding row's accordion stays OPEN after its "Mark resolved" /
 * "Mark unresolved" button is clicked.
 *
 * Owner request 2026-07-27: marking a row resolved should also CLOSE it, so the
 * row snaps back to its collapsed single-line state (check + strikethrough) and
 * the eye moves on to the next item — clearing a list should feel like clearing
 * a list.
 *
 * Un-resolving does NOT change expansion. The un-resolve button only exists
 * INSIDE the open panel, so the row is already open; auto-expanding on that
 * toggle would be the one case where the accordion moves without the user
 * asking. Expansion stays user-controlled everywhere else.
 *
 * `wasResolved` is the state BEFORE the click (i.e. which button was pressed).
 */
export function rowOpenAfterResolveToggle(open: boolean, wasResolved: boolean): boolean {
  return wasResolved ? open : false;
}

/**
 * For a STRUCTURAL finding: the section it applies to ('Experience', 'Awards'),
 * or null when it spans the whole resume — so the row can say which, instead of
 * pretending there is an item to edit.
 */
export function findingStructuralScope(finding: Finding, resume?: ResumeJSON): string | null {
  const own = finding.rawPath || '';
  if (!own || own === 'resume' || isItemAnchor(own)) return null;
  const label = humanizePath(own, resume);
  return label === own ? null : label;
}

/**
 * The quoted CONTEXT line for a non-editable (reference / structural) row: the
 * finding's own SNIPPET — the actual flagged text the linter matched — and
 * nothing else.
 *
 * Owner report 2026-07-27: a row anchored on a company entry showed the company
 * NAME as its breadcrumb AND again as the quoted context, so the flagged text
 * never appeared and the row read as "edit… what?". A reference row must never
 * quote the anchor item's own title/label back at the user — that is what the
 * breadcrumb already says. Returns null when there is no snippet, or when the
 * snippet IS just the item label (a name-level finding: the breadcrumb has it),
 * so the row shows one line, not the same string twice.
 */
export function findingReferenceQuote(finding: Finding, resume?: ResumeJSON): string | null {
  const snippet = (finding.detail || '').trim();
  if (!snippet) return null;
  const norm = (s: string) => s.replace(/[\s…"'“”]+/g, ' ').trim().toLowerCase();
  const target = norm(snippet);
  if (!target) return null;
  const labels = findingLintPaths(finding).map((p) => norm(humanizePath(p, resume)));
  return labels.includes(target) ? null : snippet;
}

/** Current text behind ANY lint path (editable or not) — '' when unresolvable. */
export function getTextAtLintPath(resume: ResumeJSON, path: string): string {
  const editable = lintPathToFindingPath(path);
  if (editable) return getTextAtPath(resume, editable);
  const role = ROLE_PATH.exec(path);
  if (role) return resume.experience?.[Number(role[1])]?.roles?.[Number(role[2])]?.title || '';
  const company = COMPANY_PATH.exec(path);
  if (company) return resume.experience?.[Number(company[1])]?.company || '';
  const indexed = INDEXED_PATH.exec(path);
  if (indexed && TITLED_LISTS[indexed[1]]) {
    const items = (resume as unknown as Record<string, { title?: string }[]>)[indexed[1]];
    return items?.[Number(indexed[2])]?.title || '';
  }
  return '';
}

/**
 * Server LintFinding → scorecard Finding. ONE mapper for every dimension, so a
 * routed style finding renders, addresses and edits exactly like an accuracy
 * one — only the dimension it is filed under differs.
 */
function mapLintFindings(findings: LintFinding[], resume?: ResumeJSON): Finding[] {
  const mapped: Finding[] = findings.map((f) => {
    const severity = effectiveSeverity(f);
    // Fold `field` into the path grammar (company NAME vs company DESCRIPTION
    // share one server path) BEFORE anything downstream addresses the item.
    // `section` is still derived from the RAW server path so the chip and the
    // frozen finding id grammar are byte-identical to previous releases.
    const canonical = lintFindingPath(f.path, f.field);
    return {
      severity,
      rule: f.rule,
      word: f.word || undefined,
      label: f.message,
      section: lintPathToSection(f.path, resume),
      detail: f.snippet || undefined,
      path: lintPathToFindingPath(canonical),
      rawPath: canonical || undefined,
      // Multi-item findings keep their full item list (absent on single-item
      // ones), so the row can offer edit + delete per involved item.
      related: f.related && f.related.length > 1
        ? f.related.map((r) => ({ ...r, path: lintFindingPath(r.path, r.field) }))
        : undefined,
      penalty: LINT_PENALTY[severity] ?? 0,
    };
  });
  mapped.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  return mapped;
}

/**
 * Accuracy ingests ONLY the lint categories routed to it (correctness +
 * consistency). A style or credibility finding is Authenticity's; a
 * completeness one is Completeness's — see `LINT_CATEGORY_DIMENSION`.
 */
function correctnessBuilder(
  allFindings: LintFinding[] | null,
  totalWords: number,
  opts?: { error?: boolean; resume?: ResumeJSON },
): DimensionBuilder {
  const label = CORRECTNESS_LABEL;
  const findings = allFindings === null ? null : lintFor(allFindings, 'correctness');
  if (findings === null) {
    return {
      key: 'correctness',
      label,
      findings: [],
      finalize: () => ({
        score: null,
        blurb: opts?.error
          ? 'Couldn’t run the accuracy checks — the rest of the scorecard is unaffected.'
          : 'Checking spelling, grammar, tense and duplicates…',
      }),
    };
  }

  const mapped = mapLintFindings(findings, opts?.resume);

  return {
    key: 'correctness',
    label,
    findings: mapped,
    finalize(kept) {
      if (totalWords === 0 && mapped.length === 0) {
        return { score: null, blurb: 'No written content yet to evaluate.' };
      }
      const penalty = kept.reduce((n, f) => n + (f.penalty || 0), 0);
      const density = (penalty / Math.max(totalWords, 150)) * 100;
      const score = Math.round(100 * Math.exp(-density / CORRECTNESS_DECAY_SCALE));

      const scored = kept.filter((f) => (f.penalty || 0) > 0).length;
      const notes = kept.length - scored;
      let blurb: string;
      if (mapped.length === 0) {
        blurb = 'No spelling, grammar, tense or duplication problems found.';
      } else if (kept.length === 0) {
        blurb = 'Everything flagged here has been marked resolved.';
      } else if (scored === 0) {
        blurb = `No errors found — ${notes} minor ${notes === 1 ? 'note' : 'notes'} listed below (they don’t affect the score).`;
      } else {
        const errs = `${scored} likely ${scored === 1 ? 'error' : 'errors'}`;
        const tail = notes > 0 ? ` (plus ${notes} minor ${notes === 1 ? 'note' : 'notes'}, unscored)` : '';
        blurb = score >= 90
          ? `${errs} to check${tail} — otherwise clean.`
          : score >= 70
            ? `${errs} found${tail} — a proofread pass is worth it.`
            : `${errs} found${tail} — typos and inconsistencies are the fastest thing to fix.`;
      }
      return { score, blurb };
    },
  };
}

/**
 * Build the Accuracy dimension (key 'correctness') from server lint findings.
 * `findings === null` means "not loaded yet" (or failed, with opts.error) →
 * score null so the overall score simply excludes it until it arrives.
 *
 * Standalone helper: ids are assigned and nothing is treated as resolved. Inside
 * `buildScorecard` the same builder is scored net-of-resolved instead.
 */
export function buildCorrectnessDimension(
  findings: LintFinding[] | null,
  totalWords: number,
  opts?: { error?: boolean; resume?: ResumeJSON },
): DimensionScore {
  const builder = correctnessBuilder(findings, totalWords, opts);
  assignIds(builder);
  return finalizeDimension(builder, new Set<string>());
}

// ── Quantification: do bullets carry concrete numbers? ──────────────────────
const NUMBERY = /\d|%|\$|\b(one|two|three|four|five|six|seven|eight|nine|ten|dozens?|hundreds?|thousands?|millions?|billions?|k\b|m\b|x\b)\b/i;

// Default share of experience bullets that should carry a metric for full marks.
// Not every bullet can (or should) be quantified, so requiring 100% is wrong:
// hitting the target = 100/100, below it scales linearly. Overridable via the
// `quantification_target_rate` global setting (see ScorecardModal).
export const DEFAULT_QUANTIFICATION_TARGET = 0.6;

function quantificationBuilder(snippets: Snippet[], target: number): DimensionBuilder {
  const bullets = snippets.filter((s) => s.isBullet && s.section.startsWith('Experience'));
  if (bullets.length === 0) {
    return {
      key: 'quantification',
      label: 'Quantification',
      findings: [],
      finalize: () => ({ score: null, blurb: 'No experience bullets to evaluate.' }),
    };
  }
  // Surface EVERY unquantified bullet (the panel scrolls) so the count matches
  // the score — no silent cap.
  const findings: Finding[] = [];
  for (const b of bullets) {
    if (!NUMBERY.test(b.text)) {
      findings.push({ severity: 'low', label: 'Bullet has no number/metric', section: b.section, detail: b.text.slice(0, 90), path: b.path, group: 'unquantified' });
    }
  }
  return {
    key: 'quantification',
    label: 'Quantification',
    findings,
    finalize(kept) {
      // A bullet the user marked resolved counts as intentionally unquantified.
      const missing = kept.filter((f) => f.group === 'unquantified').length;
      const quantified = bullets.length - missing;
      const rate = quantified / bullets.length;
      const pct = Math.round(rate * 100);
      const targetPct = Math.round(target * 100);
      // Hit the target → 100; below it scale linearly. Ceiling at 100 (over-
      // performing is fine, but doesn't earn bonus points).
      const score = target > 0 ? Math.min(100, Math.round((rate / target) * 100)) : 100;
      // Both the number AND the wording are derived from `target`, so changing
      // the global setting keeps every reference accurate.
      const blurb = rate >= target
        ? `${pct}% of your experience bullets include a concrete number or metric — at or above the recommended ${targetPct}%.`
        : `${quantified} of ${bullets.length} experience bullets (${pct}%) include a concrete number or metric, below the ${targetPct}% recommendation.`;
      return { score, blurb };
    },
  };
}

// ── Completeness: is the core resume filled in? ─────────────────────────────
/**
 * One presence check. `severity` is per-check (not a blanket 'med') because the
 * zero-out rule is global: a 'high' here zeroes Completeness. Every check below
 * is deliberately 'med' today — a missing section is a gap, not a defect — so
 * only a routed lint 'high' (too few skills) can zero this dimension.
 */
interface CompletenessCheck { ok: boolean; label: string; severity: Severity }

/**
 * Completeness also ingests the lint findings routed here by category
 * ('completeness' — e.g. `skill_count`). Each one is scored as an extra failed
 * check, so the denominator grows with what was actually evaluated.
 */
function completenessBuilder(resume: ResumeJSON, lint?: LintFinding[] | null): DimensionBuilder {
  const checks: CompletenessCheck[] = [
    { ok: !!resume.name?.trim(), label: 'Name', severity: 'med' },
    { ok: (resume.contact || []).length > 0, label: 'At least one contact method', severity: 'med' },
    { ok: !!resume.summary?.trim(), label: 'Summary', severity: 'med' },
    {
      ok: (resume.experience || []).some((c) => included(c.include) && (c.roles || []).some((r) => (r.bullets || []).some((b) => b.text?.trim()))),
      label: 'At least one experience bullet',
      severity: 'med',
    },
    { ok: (resume.skills || []).filter((s) => s.title?.trim()).length >= 3, label: 'At least 3 skills', severity: 'med' },
    { ok: (resume.education || []).length > 0, label: 'At least one education entry', severity: 'med' },
  ];
  const findings: Finding[] = checks.filter((c) => !c.ok).map((c) => ({ severity: c.severity, label: `Missing: ${c.label}`, section: 'Resume', group: 'missing_section' }));
  const routed = mapLintFindings(lintFor(lint, 'completeness'), resume);
  findings.push(...routed);
  findings.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  const total = checks.length + routed.length;
  return {
    key: 'completeness',
    label: 'Completeness',
    findings,
    finalize(kept) {
      // A resolved finding = the user says it's deliberate; count it as
      // satisfied so the score recovers.
      const passed = total - kept.length;
      const score = Math.round((passed / total) * 100);
      const blurb = routed.length === 0
        ? `${passed} of ${total} core sections present.`
        : `${passed} of ${total} completeness checks passed.`;
      return { score, blurb };
    },
  };
}

// ── Top-level ───────────────────────────────────────────────────────────────
// Owner decision 2026-07-26: Accuracy dominates — a resume with typos is not
// ready no matter how human or quantified it reads.
export const WEIGHTS: Record<DimensionScore['key'], number> = {
  completeness: 0.15, correctness: 0.45, authenticity: 0.20, quantification: 0.20,
};

/**
 * Deterministic finding IDs (dimension + section + label + occurrence) so the
 * UI's resolved-state survives scorecard rebuilds as the resume is edited.
 *
 * ID GRAMMAR IS FROZEN: `${key}|${section}|${label}|${n}`. The dimension key
 * stays 'correctness' (never 'accuracy') and labels/sections are the raw,
 * un-humanized strings — humanizing happens at render time only — so ids stay
 * byte-identical to previously persisted ones.
 */
function assignIds(dim: DimensionBuilder): void {
  const seen: Record<string, number> = {};
  for (const f of dim.findings) {
    const base = `${dim.key}|${f.section}|${f.label}`;
    const n = (seen[base] = (seen[base] || 0) + 1);
    f.id = `${base}|${n}`;
  }
}

/**
 * Score a dimension from the findings that are still UNRESOLVED, then apply the
 * zero-out rule: a single unresolved 'high' finding drops the dimension to 0.
 */
function finalizeDimension(dim: DimensionBuilder, resolved: Set<string>): DimensionScore {
  const kept = dim.findings.filter((f) => !f.id || !resolved.has(f.id));
  const { score, blurb } = dim.finalize(kept);
  const highs = kept.filter((f) => f.severity === 'high').length;
  if (score !== null && highs > 0) {
    const isOne = highs === 1;
    return {
      key: dim.key,
      label: dim.label,
      score: 0,
      blurb: `${highs} unresolved high-severity ${isOne ? 'item' : 'items'} — this dimension scores 0 until ${isOne ? 'it is' : 'they are'} fixed or marked resolved. ${blurb}`,
      findings: dim.findings,
      zeroedBy: highs,
    };
  }
  return { key: dim.key, label: dim.label, score, blurb, findings: dim.findings };
}

export function buildScorecard(
  resume: ResumeJSON,
  opts?: {
    ruleOfThreeTolerance?: number;
    quantificationTarget?: number;
    /** Server lint findings; `null`/omitted = still loading (score null). */
    lintFindings?: LintFinding[] | null;
    /** The lint request failed — show the error blurb instead of "Checking…". */
    lintError?: boolean;
    /**
     * Finding ids the user marked resolved. Resolved findings are removed from
     * EVERY calculation (penalties, densities, counts, blurbs and the zero-out
     * rule) — they are still listed in the UI, struck through.
     */
    resolvedIds?: Iterable<string> | null;
  },
): Scorecard {
  const tolerance = opts?.ruleOfThreeTolerance ?? DEFAULT_RULE_OF_THREE_TOLERANCE;
  const quantTarget = opts?.quantificationTarget ?? DEFAULT_QUANTIFICATION_TARGET;
  const resolved = new Set<string>(opts?.resolvedIds ? Array.from(opts.resolvedIds) : []);
  const snippets = collectProse(resume);
  const proseWords = snippets.reduce((n, s) => n + wordCount(s.text), 0);
  // Line-up order (owner decision 2026-07-26): Completeness, Accuracy,
  // Authenticity, Quantification.
  // Every builder is handed the FULL lint list and takes only the categories
  // routed to it (`lintFor`), so a finding can never be double-counted or
  // silently dropped.
  const lint = opts?.lintFindings ?? null;
  const builders: DimensionBuilder[] = [
    completenessBuilder(resume, lint),
    correctnessBuilder(lint, proseWords, { error: opts?.lintError, resume }),
    authenticityBuilder(snippets, tolerance, lint, resume),
    quantificationBuilder(snippets, quantTarget),
  ];
  // IDs first — scores are computed net-of-resolved, so identity must exist
  // before any scoring happens.
  for (const b of builders) assignIds(b);
  const dimensions = builders.map((b) => finalizeDimension(b, resolved));

  let wsum = 0, w = 0;
  for (const d of dimensions) {
    if (d.score === null) continue;
    wsum += d.score * WEIGHTS[d.key];
    w += WEIGHTS[d.key];
  }
  const overall = w === 0 ? 0 : Math.round(wsum / w);
  return { overall, dimensions };
}

// ── Per-finding recheck (immediate in-place feedback after an inline save) ──
//
// When a user edits ONE flagged item in the Scorecard and clicks that row's
// Save, they need to know instantly whether the fix worked. Rechecking is
// deliberately scoped to that ONE finding: we recompute its OWN dimension from
// the updated resume and ask "is this finding still there?" — the visible list
// is never rebuilt underneath the user (reopening the modal does that).

export type RecheckResult = 'resolved' | 'flagged';

/**
 * The dimension a finding belongs to, read back out of its (frozen) id grammar
 * `${key}|${section}|${label}|${occurrence}`. Returns null for a finding that
 * never went through `assignIds`.
 */
export function findingDimension(f: Finding): DimensionScore['key'] | null {
  const key = f.id?.split('|')[0];
  return key === 'authenticity' || key === 'correctness' || key === 'quantification' || key === 'completeness'
    ? key
    : null;
}

/**
 * Identity of a finding that deliberately IGNORES the occurrence suffix of its
 * id, and adds the item it points at.
 *
 * Why: ids carry a 1-based occurrence counter per (dimension, section, label).
 * If the same section+label is flagged N times and the user fixes ONE of them,
 * the survivors RENUMBER (`…|2` becomes `…|1`), so naive id matching would
 * report the wrong row as resolved. Comparing COUNTS of this occurrence-free
 * key before vs after the edit is immune to renumbering, and including the
 * item path keeps two different bullets in the same company distinct.
 */
export function findingMatchKey(f: Finding): string {
  const path = f.rawPath || (f.path ? findingPathToLintPath(f.path) : '');
  return `${f.section}\u0000${f.label}\u0000${path}`;
}

function countMatching(findings: Finding[], key: string): number {
  let n = 0;
  for (const f of findings) if (findingMatchKey(f) === key) n++;
  return n;
}

/**
 * Did this finding go away? True when the updated dimension has FEWER findings
 * carrying this finding's occurrence-free key than the pre-edit dimension had.
 * (The pre-edit list always contains the finding itself, so "count dropped to
 * zero" is just the common case of "count dropped".)
 */
export function isFindingResolved(finding: Finding, before: Finding[], after: Finding[]): boolean {
  const key = findingMatchKey(finding);
  return countMatching(after, key) < countMatching(before, key);
}

export interface RecheckOptions {
  ruleOfThreeTolerance?: number;
  quantificationTarget?: number;
  /** Lint findings already on hand — reused as-is for the local dimensions. */
  lintFindings?: LintFinding[] | null;
  /**
   * Re-run the server lint against the updated resume. Called for any
   * LINT-SOURCED finding — which, since category routing, can sit in Accuracy,
   * Authenticity or Completeness (one cheap, LLM-free request per row-save);
   * may reject, in which case the caller decides what to show.
   */
  relint?: (resume: ResumeJSON) => Promise<LintFinding[]>;
}

/**
 * Recheck ONE finding against the resume as it now stands.
 * Browser-computed findings are rebuilt locally; a LINT-SOURCED one costs one
 * `relint` call — including the style/credibility/completeness lint findings
 * that category routing files under Authenticity and Completeness, whose
 * dimension is otherwise rebuilt from stale server findings.
 */
export async function recheckFinding(
  finding: Finding,
  beforeFindings: Finding[],
  updatedResume: ResumeJSON,
  opts: RecheckOptions = {},
): Promise<RecheckResult> {
  const dim = findingDimension(finding);
  if (!dim) return 'flagged';
  let lint = opts.lintFindings ?? null;
  // `rule` is set only on lint-sourced findings; Accuracy carries nothing else.
  if (dim === 'correctness' || finding.rule) {
    if (!opts.relint) return 'flagged';
    lint = await opts.relint(updatedResume);
  }
  const after = buildScorecard(updatedResume, {
    ruleOfThreeTolerance: opts.ruleOfThreeTolerance,
    quantificationTarget: opts.quantificationTarget,
    lintFindings: lint,
  }).dimensions.find((d) => d.key === dim)?.findings ?? [];
  return isFindingResolved(finding, beforeFindings, after) ? 'resolved' : 'flagged';
}

// ── "Fix all safe issues" (Recruiter Check BR-2) ───────────────────────────
//
// The linter marks a finding `can_fix` ONLY when its `suggestion` is the whole
// corrected field value and the rewrite is mechanically safe (spelling, doubled
// words, an unambiguous past-tense opener — never an ambiguous noun opener like
// "Lead architect"; see the noun-compound denylist in resume_lint.py). Those,
// and only those, are what this batch applies.
//
// Everything here is pure so the (destructive) rules are unit-testable:
//   • a finding the user marked RESOLVED is never auto-fixed — dismissing means
//     "leave this alone";
//   • a suggestion equal to the current text is dropped (nothing to do);
//   • at most ONE fix per item: each `suggestion` is a full replacement computed
//     from the text as it was, so applying two to the same bullet would discard
//     the first. The dropped one simply reappears on the next lint pass.

/** One before→after row in the Fix-all checklist. */
export interface SafeFix {
  /** Stable within one pass — checkbox identity. */
  key: string;
  rule: string;
  /** Canonical lint path grammar this fix writes to. */
  lintPath: string;
  path: FindingPath;
  /** The linter's own message, shown as the reason. */
  label: string;
  section: string;
  before: string;
  after: string;
}

/** `rule|path|message` — the join key between a lint finding and its Finding. */
function safeFixKey(rule: string, lintPath: string, message: string): string {
  return `${rule}|${lintPath}|${message}`;
}

/**
 * The keys of findings the user has marked RESOLVED, so the batch can skip
 * them. Derived through `buildScorecard` because that is where resolved ids are
 * assigned; the join is (rule, canonical path, message).
 */
export function resolvedSafeFixKeys(
  resume: ResumeJSON,
  findings: LintFinding[] | null,
  resolvedIds?: Iterable<string> | null,
): Set<string> {
  const resolved = new Set<string>(resolvedIds ? Array.from(resolvedIds) : []);
  const keys = new Set<string>();
  if (!findings || !resolved.size) return keys;
  buildScorecard(resume, { lintFindings: findings, resolvedIds: resolved })
    .dimensions
    .flatMap((d) => d.findings)
    .forEach((f) => {
      if (f.rule && f.id && resolved.has(f.id)) {
        keys.add(safeFixKey(f.rule, f.rawPath || '', f.label));
      }
    });
  return keys;
}

/** Every safe mechanical fix available on this resume, in lint order. */
export function collectSafeFixes(
  resume: ResumeJSON,
  findings: LintFinding[] | null,
  skipKeys?: ReadonlySet<string> | null,
): SafeFix[] {
  if (!findings) return [];
  const out: SafeFix[] = [];
  const claimed = new Set<string>();
  for (const f of findings) {
    if (!f.can_fix || typeof f.suggestion !== 'string') continue;
    const lintPath = lintFindingPath(f.path, f.field);
    const key = safeFixKey(f.rule, lintPath, f.message);
    if (skipKeys?.has(key)) continue;
    const path = lintPathToFindingPath(lintPath);
    if (!path) continue;             // not an item this editor can write
    if (claimed.has(lintPath)) continue; // one rewrite per item, see above
    const before = getTextAtPath(resume, path);
    if (!before || before === f.suggestion) continue;
    claimed.add(lintPath);
    out.push({
      key,
      rule: f.rule,
      lintPath,
      path,
      label: f.message,
      section: lintPathToSection(f.path, resume),
      before,
      after: f.suggestion,
    });
  }
  return out;
}

/**
 * Apply a batch in ONE immutable pass. The caller saves ONCE afterwards — a
 * per-item save would re-trigger the resume tag lifecycle N times (BR-2).
 * Each fix is skipped if the text moved under it, so a stale row can never
 * overwrite a newer edit.
 */
export function applySafeFixes(resume: ResumeJSON, fixes: SafeFix[]): ResumeJSON {
  return fixes.reduce((acc, fix) => (
    getTextAtPath(acc, fix.path) === fix.before
      ? setTextAtPath(acc, fix.path, fix.after)
      : acc
  ), resume);
}

export function scoreColor(score: number | null): { text: string; bg: string; bar: string } {
  if (score === null) return { text: 'text-gray-400', bg: 'bg-gray-50', bar: 'bg-gray-300' };
  if (score >= 85) return { text: 'text-green-700', bg: 'bg-green-50', bar: 'bg-green-500' };
  if (score >= 70) return { text: 'text-amber-700', bg: 'bg-amber-50', bar: 'bg-amber-500' };
  return { text: 'text-red-700', bg: 'bg-red-50', bar: 'bg-red-500' };
}
